import { useEffect, useMemo, useRef, useState } from "react";
import { Ring } from "@uiball/loaders";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch, getState } from "../../../../state/store";
import {
  deleteAppLocaliztion,
  setIsFileUploadDeclined,
  setIsMissingKeysModalActive,
  setIsNeedRefetchLocales,
  setLocaleKeys,
  setLocalizationToApp,
  setMissingKeysModalData,
  setMissingKeysModalLocalization,
  setPrevLocale
} from "../../../../state/ducks";
import {
  selectEditingLocale,
  selectOriginalDefaultLocale,
  selectSelectedAppLocalizations
} from "../../../../state/selectors";

import { languages, strCode } from "../../../../consts";
import { appsService, log, notificationService, t } from "../../../../services";
import { responseUtil } from "../../../../utils";
import { localisationTabUtils } from "../utils/localisationTabUtils";
import { useIsLocalisationEditing } from "../hooks/useIsLocalisationEditing";

import {
  DiscardModal,
  EditButtonsGroup,
  FlexContainers,
  IconButton,
  SmallButton,
  SVG,
  WrapperContainer
} from "../../../../components";

export const ControlButtonsCell = ({
  cell,
  getEditingLocalization,
  setEditedLocalization,
  getOriginalDefaultLocalization,
  setOriginalDefaultLocalization,
  setLocalizationToRefetch
}) => {
  const [deleteModalActive, setDeleteModalActive] = useState(false);

  const selectedApp = useSelector((store) => store.apps.selectedApp);
  const prevLocale = useSelector((store) => store.apps.prevLocale);
  const isMissingKeysModalActive = useSelector((store) => store.apps.isMissingKeysModalActive);
  const missingKeysModalLocalization = useSelector(
    (store) => store.apps.missingKeysModalLocalization
  );
  const isFileUploadDeclined = useSelector((store) => store.apps.isFileUploadDeclined);

  const [loading, setLoading] = useState(false);
  const [fileData, setFileData] = useState(null);

  const localizations = useMemo(() => selectedApp.localizations, [selectedApp]);

  const initialCellValue = useRef(cell.row.original);
  const fileInputRef = useRef(null);

  const language = useMemo(() => {
    const lang = cell.row.original?.language;
    const languageItem = languages?.find((el) => el?.id === lang);

    return languageItem;
  }, [cell.row.original.language]);

  const { isEdit, isLocalizationEditing } = useIsLocalisationEditing({
    originalRow: cell.row.original,
    getEditingLocalization
  });

  const isNew = useMemo(() => {
    return cell.row.original?.isNew;
  }, [cell.row.original?.isNew]);

  const isNameValid = useMemo(() => {
    return localisationTabUtils.validateName(cell.row.original.name);
  }, [cell.row.original.name]);

  const showDeleteBtn = useMemo(
    () => (isEdit ? !isLocalizationEditing : true),
    [isEdit, isLocalizationEditing]
  );

  const isDisabled = useMemo(() => {
    if (!cell.row.original.path) {
      return false;
    }

    if (!isEdit && localizations?.length === 1 && !fileData && !cell.row.original.path) {
      return false;
    }

    if (cell.row.original.isDefault) {
      return true;
    }

    if (prevLocale?.name === cell.row.original.name && !isEdit && cell.row.original.path) {
      return false;
    }

    return prevLocale?.name === cell.row.original.name;
  }, [cell, prevLocale, isEdit]);

  const isDisableEditing = useMemo(() => {
    const editingLocalization = getEditingLocalization();

    return editingLocalization && (!editingLocalization.name || !editingLocalization.path);
  }, [getEditingLocalization]);

  useEffect(() => {
    if (!getEditingLocalization()) {
      initialCellValue.current = cell.row.original;
    }
  }, [getEditingLocalization]);

  const handleFileInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = async () => {
      const fileContent = reader.result;
      setFileData(fileContent);

      await processLocalization(fileContent);
    };

    setLoading(true);
    reader.readAsText(file);
  };

  const processLocalization = async (fileContent) => {
    try {
      const jsonData = JSON.parse(fileContent);

      const missingKeys = localisationTabUtils.calculateMissingKeys(
        Object.keys(selectedApp.localizationKeys || {}),
        Object.keys(jsonData)
      );

      if (Object.keys(missingKeys).length && !missingKeysModalLocalization) {
        dispatch(setMissingKeysModalData(Object.keys(missingKeys)));
        dispatch(setIsMissingKeysModalActive(true));
        dispatch(setMissingKeysModalLocalization(cell.row.original));

        return;
      }

      const name = cell.row.original.name;
      const localeName = cell.row.original.language;

      const index = localizations.findIndex((loc) => loc.name === name);
      const currentLocalization = localizations[index];

      if (!isEdit) {
        await appsService.uploadLocalization(
          selectedApp.appType,
          localeName,
          name,
          localizations.length === 1 || cell.row.original.isDefault,
          jsonData
        );

        dispatch(setMissingKeysModalLocalization(null));

        const newLocalization = {
          ...currentLocalization,
          path: `${selectedApp.appType}/${name}.json`
        };

        if (cell.row.original.isDefault) {
          dispatch(setLocaleKeys(jsonData));

          setOriginalDefaultLocalization(currentLocalization);
          dispatch(setPrevLocale(currentLocalization));
          dispatch(setIsNeedRefetchLocales(true));
        }

        setEditedLocalization(null);

        updateLocalization(index, newLocalization);
      } else {
        const updatedLocalization = {
          ...currentLocalization,
          path: `${selectedApp.appType}/${name}.json`
        };

        updateLocalization(index, updatedLocalization);
        dispatch(setMissingKeysModalLocalization(null));
      }

      if (!isEdit) {
        setLocalizationToRefetch(cell.row.original);
      }
    } catch (err) {
      log.error("LocalisationTabTableColumns: Failed to upload localization:", err);
      notificationService.showError({
        title: t(strCode.LOCALIZATION_UPLOAD_ERROR_TITLE),
        message: err?.response?.data?.message || err?.toString()
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLocalization = (index, localization) => {
    const data = [...localizations];
    data[index] = localization;
    dispatch(setLocalizationToApp(data));
  };

  const deleteLocalization = async () => {
    setLoading(true);

    try {
      if (cell.row.original.path) {
        await appsService.deleteLocalization({
          appType: selectedApp.appType,
          localization: cell.row.original.name
        });
      }

      if (getEditingLocalization()) {
        setEditedLocalization(null);
      }

      if (cell.row.original.isDefault) {
        const originalDefaultLocalization = getOriginalDefaultLocalization();

        const newLocalizations = [...localizations].map((localization) => {
          if (localization.name === originalDefaultLocalization?.name) {
            return {
              ...localization,
              isDefault: true
            };
          }
          return localization;
        });

        dispatch(setLocalizationToApp(newLocalizations));
      }

      dispatch(deleteAppLocaliztion(cell.row.original.name));

      notificationService.showSuccess({
        title: t(strCode.LOCALIZATION_DELETE_TITLE)
      });
    } catch (e) {
      log.error("LocalisationTabTableColumns: Failed to delete localization:", e);
      notificationService.showError({
        title: t(strCode.LOCALIZATION_DELETE_ERROR),
        message: e?.response?.data?.message ? e.response.data.message : e?.toString()
      });
    } finally {
      setLoading(false);
      setDeleteModalActive(false);
    }
  };

  const uploadByAcceptBtn = async () => {
    setLoading(true);
    const name = cell.row.original.name;

    const currentLocalization = localizations.find((localization) => localization.name === name);

    if (fileData) {
      try {
        const jsonData = JSON.parse(fileData);
        const localeName = cell.row.original.language;

        const index = localizations.findIndex((localization) => localization.name === name);

        const lang = languages.find((lang) => lang.id === localeName);
        const data = [...localizations];

        await appsService.uploadLocalization(
          selectedApp.appType,
          localeName,
          name,
          currentLocalization.isDefault,
          jsonData
        );

        data[index] = {
          ...currentLocalization,
          isNew: false,
          isEdit: false,
          path: `${selectedApp.appType}/${name}.json`
        };

        if (cell.row.original.isDefault) {
          dispatch(setLocaleKeys(jsonData));
          dispatch(setIsNeedRefetchLocales(true));
        }

        setLocalizationToRefetch(data[index]);

        dispatch(setLocalizationToApp(data));

        if (!getEditingLocalization()?.name) {
          notificationService.showSuccess({
            title: t(strCode.LOCALIZATION_ADDED_TITLE, { id: lang.id, value: lang.value })
          });
        } else {
          notificationService.showSuccess({
            title: t(strCode.APP_UPDATED_SHORT_TITLTE),
            message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name }),
            immediate: false
          });
        }
      } catch (e) {
        log.error("LocalisationTabTableColumns: Failed to upload localization:", e);
        notificationService.showError({
          title: t(strCode.LOCALIZATION_UPLOAD_ERROR_TITLE),
          message: e?.response?.data?.message ? e.response.data.message : e?.toString()
        });

        setLoading(false);

        return;
      }
    }

    if (cell.row.original.isDefault !== getEditingLocalization()?.isDefault) {
      try {
        const appLocalizations = selectedApp.localizations;

        const localizationData = appLocalizations.map((localization) => ({
          ...localization,
          isDefault: localization.name === cell.row.original.name && cell.row.original.isDefault
        }));

        const data = localizationData.map(({ name, language, isDefault }) => ({
          name,
          language,
          isDefault
        }));

        const res = await appsService.updateApp({
          name: selectedApp.name,
          appType: selectedApp.appType,
          localizations: data
        });

        if (responseUtil.isResponseSuccessful(res) && !fileData) {
          notificationService.showSuccess({
            title: t(strCode.APP_UPDATED_SHORT_TITLTE),
            message: t(strCode.APP_UPDATED_LONG_TITLTE, { name: selectedApp.name }),
            immediate: false
          });

          dispatch(setIsNeedRefetchLocales(true));
        }

        if (!res) {
          setLoading(false);

          return;
        }
      } catch (err) {
        console.error("Error updating localization:", err);
      }
    }

    if (currentLocalization.isDefault) {
      setOriginalDefaultLocalization(currentLocalization);
      dispatch(setPrevLocale(currentLocalization));
    }

    setEditedLocalization(null);
    setLoading(false);
    setFileData(null);
  };

  const onHandleCancel = () => {
    const appLocalizations = selectSelectedAppLocalizations()(getState());
    const edited = selectEditingLocale()(getState());
    const defaultLocle = selectOriginalDefaultLocale()(getState());

    setEditedLocalization(null);
    setFileData(null);

    const updatedLocalizations = [...appLocalizations]
      .map((localization, i) => {
        if (cell.row.index === i && edited) {
          return { ...edited };
        }

        return {
          ...localization,
          isNew: false,
          isEdit: false,
          isDefault: localization.name === defaultLocle?.name
        };
      })
      .filter((localization) => {
        return localization.language && localization.name && localization.path;
      });

    dispatch(setLocalizationToApp(updatedLocalizations));
  };

  const onHandleEdit = () => {
    if (!getEditingLocalization()) {
      setEditedLocalization({ ...cell.row.original, isNew: false, isEdit: true });
    }
  };

  useEffect(() => {
    // Means user saw pop up with missing keys and click "Continue"
    if (
      missingKeysModalLocalization &&
      fileData &&
      !isMissingKeysModalActive &&
      missingKeysModalLocalization.language === cell.row.original.language
    ) {
      setLoading(true);
      processLocalization(fileData);
    }

    if (isFileUploadDeclined) {
      setFileData(null);
      dispatch(setIsFileUploadDeclined(false));
    }
  }, [missingKeysModalLocalization, isMissingKeysModalActive, fileData, isFileUploadDeclined]);

  useEffect(() => {
    return () => {
      if (isLocalizationEditing && isEdit) {
        onHandleCancel();
      }
    };
  }, []);

  return (
    <>
      <WrapperContainer Container={FlexContainers.FlexRowEnd} width="100%" gap={8}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".json"
          onChange={handleFileChange}
          onClick={(event) => (event.target.value = null)}
        />

        {!loading ? (
          <>
            {isLocalizationEditing && (
              <SmallButton
                secondary
                secondaryBold
                small
                disabled={!!isNew && !isNameValid}
                onClick={handleFileInputClick}>
                <WrapperContainer Container={FlexContainers.FlexRowCenter} gap={"5px"}>
                  <SVG.UploadIcon />
                  {t(strCode.UPLOAD)}
                </WrapperContainer>
              </SmallButton>
            )}

            {showDeleteBtn && (
              <IconButton
                hasHoverCircle
                isLarge
                disabled={isDisabled}
                onClick={() => setDeleteModalActive(true)}
                tooltipLabel={isDisabled ? t(strCode.DEFAULT_LANGUAGE_WARNING) : null}
                xShift={isEdit ? -100 : -150}>
                <SVG.DeleteIcon />
              </IconButton>
            )}

            {isEdit && (
              <WrapperContainer>
                <EditButtonsGroup
                  disableEdit={isDisableEditing}
                  handleEdit={onHandleEdit}
                  isEdit={!isLocalizationEditing}
                  handleCancel={onHandleCancel}
                  handleAccept={uploadByAcceptBtn}
                  disableAccept={
                    (!fileData &&
                      cell.row.original.isDefault === getEditingLocalization()?.isDefault &&
                      cell.row.original.language === getEditingLocalization()?.language) ||
                    (isLocalizationEditing &&
                      (!cell.row.original.language ||
                        (!!isNew && !isNameValid) ||
                        (!fileData && !initialCellValue.current.path)))
                  }
                />
              </WrapperContainer>
            )}
          </>
        ) : (
          <Ring size={18} lineWeight={5} speed={2} />
        )}
      </WrapperContainer>

      <DiscardModal
        title={t(strCode.REMOVE_APP_LOCALIZATION_TITLE, {
          language: language?.value ?? cell.row.original.language
        })}
        text={t(strCode.REMOVE_APP_LOCALIZATION_INFO, {
          language: language?.id
            ? `${language.id} - ${language?.value}`
            : cell.row.original.language
        })}
        loading={loading}
        confirmBtnText={strCode.DELETE_BTN}
        active={deleteModalActive}
        setActive={setDeleteModalActive}
        onDiscard={() => {
          deleteLocalization();
        }}
      />
    </>
  );
};

ControlButtonsCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.string.isRequired,
    row: PropTypes.shape({
      index: PropTypes.number.isRequired,
      original: PropTypes.shape({
        path: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        language: PropTypes.string.isRequired,
        isDefault: PropTypes.bool.isRequired,
        isNew: PropTypes.bool
      }).isRequired
    }).isRequired
  }).isRequired,

  getEditingLocalization: PropTypes.func.isRequired,
  setEditedLocalization: PropTypes.func.isRequired,
  getOriginalDefaultLocalization: PropTypes.func.isRequired,
  setOriginalDefaultLocalization: PropTypes.func.isRequired,
  setLocalizationToRefetch: PropTypes.func.isRequired
};
